import {
  NODE_PROPERTY_SCHEMAS,
  REQUIRED_PROVENANCE_FIELDS,
  type NodeLabel
} from '@atbash-labs/ontology-core'

/** Neo4j / ingest bookkeeping keys — never shown as user-facing properties. */
const PLANT_NODE_SYSTEM_KEYS: ReadonlySet<string> = new Set([
  'id',
  '_id',
  'valid',
  'metadata',
  'provenance',
  'lastIngestJobId',
  'lastIngestWriteId',
  'lastIngestAt',
  'label'
])

const PLANT_PROVENANCE_KEYS: ReadonlySet<string> = new Set(
  REQUIRED_PROVENANCE_FIELDS.map((key) => key.toUpperCase())
)

export type PlantAdditionalProperty = {
  key: string
  value: string
}

const PLANT_ADDITIONAL_PROPERTY_EXCLUDE_KEYS_BY_LABEL: Partial<
  Record<NodeLabel, ReadonlySet<string>>
> = {
  Site: new Set(['DISPLAYNAME']),
  Controller: new Set(['DISPLAYNAME']),
  DcsController: new Set(['DISPLAYNAME']),
  Cee: new Set(['DISPLAYNAME']),
  FunctionBlock: new Set(['DISPLAYNAME']),
  IoModule: new Set(['DISPLAYNAME']),
  IoChannel: new Set(['DISPLAYNAME']),
  Program: new Set(['DISPLAYNAME']),
  Routine: new Set(['DISPLAYNAME']),
  AOI: new Set(['DISPLAYNAME']),
  PLCTag: new Set(['DISPLAYNAME']),
  Line: new Set(['DISPLAYNAME']),
  Equipment: new Set(['DISPLAYNAME']),
  Process: new Set(['DISPLAYNAME'])
}

function formatPropertyValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function normalizePropertyKey(key: string): string {
  return key.toUpperCase()
}

function isEmptyPropertyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  return (
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype &&
    Object.keys(value as Record<string, unknown>).length === 0
  )
}

/**
 * Collect ad-hoc node properties that are not declared in
 * `NODE_PROPERTY_SCHEMAS` for the given label. Used by Plant Viewer detail
 * pages to surface LLM-written metadata under "Additional Properties".
 */
export function collectAdditionalProperties(
  label: NodeLabel,
  raw: Record<string, unknown>,
  excludeKeys: Iterable<string> = []
): PlantAdditionalProperty[] {
  const schema = NODE_PROPERTY_SCHEMAS[label]
  const labelExcludeKeys: ReadonlySet<string> =
    PLANT_ADDITIONAL_PROPERTY_EXCLUDE_KEYS_BY_LABEL[label] ?? new Set()
  const knownKeys = new Set<string>([
    ...Object.keys(schema?.properties ?? {}),
    ...PLANT_NODE_SYSTEM_KEYS,
    ...excludeKeys
  ])
  const out: PlantAdditionalProperty[] = []
  for (const [key, value] of Object.entries(raw).sort(([a], [b]) => a.localeCompare(b))) {
    if (knownKeys.has(key)) continue
    const normalizedKey = normalizePropertyKey(key)
    if (PLANT_PROVENANCE_KEYS.has(normalizedKey)) continue
    if (labelExcludeKeys.has(normalizedKey)) continue
    if (isEmptyPropertyValue(value)) continue
    out.push({ key, value: formatPropertyValue(value) })
  }
  return out
}
